import { Controller } from "react-hook-form";
import { GeoData } from "@rogoag/shared-ts";
import {
	TextField,
	Checkbox,
	Select,
	MenuItem,
	Button,
	Box,
	Typography,
	Accordion,
	AccordionDetails,
	Autocomplete,
	Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AccordianSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormField from "../../helpers/FormField";
import { CREATE_NEW_GROWER, CREATE_NEW_SALESPERSON } from "../../../utilities/constants";
import PortalTooltip from "../../helpers/PortalTooltip";
import { LoadingComponent } from "../../helpers/LoadingComponent";
import type { Grower, Job } from "../../../api/types";
import { Publish, Upload } from "@mui/icons-material";
import {
	getFormValidationRules,
	isFieldRequired,
	isFieldVisible,
	ValidationContext,
	FileType,
} from "../../../utilities/jobValidation";
import { ImportTableColumn, getJobPriorities } from "../../../utilities/types";
import { columnDisplayNames } from "../../admin/ImportSettings";
import { useImportFormManualViewModel } from "./ImportForm_Manual.vm";
import { ManualEntryFormProps } from "./ImportForm_Manual.types";
import "./ImportForm_Manual.css";

export function ManualEntryForm(props: ManualEntryFormProps) {
	const vm = useImportFormManualViewModel(props);

	function fileFormField(
		title: string,
		helpText: string,
		key: keyof Job["files"],
		fieldType: FileType | undefined,
		data: Job,
	) {
		// Build file validation context with all necessary business logic inputs
		const fileContext: ValidationContext = {
			...vm.validationContext,
			isPointsAttached: vm.watch("isPointsAttached"),
		};

		// Use centralized validation logic
		const required = fieldType ? isFieldRequired(fieldType, fileContext) : false;
		const hidden = fieldType ? !isFieldVisible(fieldType, fileContext) : false;

		if (hidden) return null;

		const handleRemoveFile = async (index: number) => {
			const updatedFiles = Array.from(data.files[key] as File[]);
			updatedFiles.splice(index, 1);
			vm.setValue(`files.${key}`, updatedFiles.length ? updatedFiles : undefined);

			// Also clear GeoJSON when file is removed
			if (fieldType?.endsWith("GeoJSON")) {
				if (updatedFiles.length === 0) {
					vm.setValue(`files.${fieldType}`, undefined);
				}
			}

			// Trigger validation after removing file
			await vm.trigger(`files.${key}`);
		};

		// Extract onChange from register to wrap it with validation trigger
		const registerProps = vm.register(`files.${key}`, {
			required: required ? "File upload required for Job/Configuration type" : undefined,
		});
		const { onChange: rhfOnChange, ...restRegisterProps } = registerProps;

		return (
			<>
				<FormField
					title={title}
					required={required}
					helpText={helpText}
					// @ts-ignore
					error={vm.errors[key]?.message}
					input={
						<Box className="file-upload-box">
							<input
								accept="*/*"
								className="file-input-hidden"
								id={`contained-button-file-${key}`}
								multiple
								type="file"
								{...restRegisterProps}
								onChange={async (e) => {
									await rhfOnChange(e); // Call react-hook-form's onChange first
									await vm.trigger(`files.${key}`); // Then trigger validation

									// Convert to GeoJSON if this is a geo file field
									const files = e.target.files;
									if (files && files.length > 0 && fieldType?.endsWith("GeoJSON")) {
										try {
											const geoJSON = await GeoData.toGeoJSON(files[0], "SHP");
											vm.setValue(`files.${fieldType}`, geoJSON);
										} catch (error) {
											console.error(`Failed to convert ${key} to GeoJSON:`, error);
										}
									}
								}}
							/>
							<label htmlFor={`contained-button-file-${key}`}>
								<Button variant="contained" component="span">
									<Upload className="icon-in-button" /> Upload
								</Button>
							</label>
							{data.files && data.files[key] && (
								<div>
									{Array.from(data.files[key] ? (data.files[key] as File[]) : []).map((file, index) => (
										<Box key={index} className="file-item">
											<Typography>{file.name}</Typography>
											<Button onClick={() => handleRemoveFile(index)} variant="outlined" color="error" size="small">
												Remove
											</Button>
										</Box>
									))}
								</div>
							)}
						</Box>
					}
				/>
			</>
		);
	}

	function textFormField(
		title: string,
		helpText: string,
		validationKey: ImportTableColumn,
		options?: {
			registerName?: string;
			multiline?: boolean;
			rows?: number;
			type?: string;
			size?: "small" | "medium";
			value?: any;
			onChangeCallback?: () => void;
		},
	) {
		const registerName = options?.registerName || (validationKey as string);
		const error = vm.errors[registerName as keyof Job]?.message;

		// Get the register props and merge with custom onChange
		const registerProps = vm.register(registerName as any, getFormValidationRules(validationKey, vm.validationContext));
		const { onChange: rhfOnChange, ...restRegisterProps } = registerProps;

		return (
			<FormField
				title={title}
				error={error}
				required={isFieldRequired(validationKey, vm.validationContext)}
				hidden={!isFieldVisible(validationKey, vm.validationContext)}
				helpText={helpText}
				input={
					<TextField
						variant="outlined"
						color={error ? "error" : "primary"}
						fullWidth
						{...(options?.size && { size: options.size })}
						{...(options?.value !== undefined && { value: options.value })}
						{...(options?.multiline && { multiline: true, rows: options.rows })}
						{...(options?.type && { type: options.type })}
						{...restRegisterProps}
						onChange={(e) => {
							rhfOnChange(e); // Call react-hook-form's onChange first
							if (options?.onChangeCallback) {
								options.onChangeCallback(); // Then call custom callback
							}
						}}
					/>
				}
			/>
		);
	}

	// Helper function to render checkbox FormField components with validation
	function checkboxFormField(
		title: string,
		helpText: string,
		validationKey: ImportTableColumn,
		options?: {
			registerName?: string;
		},
	) {
		const registerName = options?.registerName || (validationKey as string);

		return (
			<Controller
				name={registerName as any}
				control={vm.control}
				render={({ field }) => (
					<FormField
						title={title}
						helpText={helpText}
						required={isFieldRequired(validationKey, vm.validationContext)}
						hidden={!isFieldVisible(validationKey, vm.validationContext)}
						input={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
					/>
				)}
			/>
		);
	}

	// Helper function to render date FormField components with validation
	function dateFormField(
		title: string,
		helpText: string,
		validationKey: ImportTableColumn,
		options?: {
			registerName?: string;
		},
	) {
		const registerName = options?.registerName || (validationKey as string);
		const error = vm.errors[registerName as keyof Job]?.message;

		return (
			<Controller
				name={registerName as any}
				control={vm.control}
				rules={getFormValidationRules(validationKey, vm.validationContext)}
				render={({ field }) => (
					<FormField
						title={title}
						helpText={helpText}
						required={isFieldRequired(validationKey, vm.validationContext)}
						hidden={!isFieldVisible(validationKey, vm.validationContext)}
						error={error}
						input={
							<TextField
								variant="outlined"
								type="date"
								fullWidth
								InputLabelProps={{ shrink: true }}
								error={!!error}
								value={field.value ? new Date(field.value).toISOString().split("T")[0] : ""}
								onChange={(e) => {
									const dateValue = e.target.value ? new Date(e.target.value) : undefined;
									field.onChange(dateValue);
								}}
							/>
						}
					/>
				)}
			/>
		);
	}

	// Helper function to render autocomplete FormField components with validation
	function autocompleteFormField<T = string>(
		title: string,
		helpText: string,
		validationKey: ImportTableColumn,
		autocompleteOptions: T[],
		options?: {
			registerName?: string;
			getOptionLabel?: (option: T) => string;
			customValue?: any;
			customOnChange?: (newValue: T | null, field: any) => void;
		},
	) {
		const registerName = options?.registerName || (validationKey as string);
		const error = vm.errors[registerName as keyof Job]?.message;

		return (
			<Controller
				name={registerName as any}
				control={vm.control}
				rules={getFormValidationRules(validationKey, vm.validationContext)}
				render={({ field }) => (
					<FormField
						title={title}
						helpText={helpText}
						required={isFieldRequired(validationKey, vm.validationContext)}
						hidden={!isFieldVisible(validationKey, vm.validationContext)}
						error={error}
						input={
							<Autocomplete
								fullWidth
								options={autocompleteOptions}
								value={options?.customValue !== undefined ? options.customValue : field.value || null}
								onChange={(_, newValue) => {
									if (options?.customOnChange) {
										options.customOnChange(newValue, field);
									} else {
										field.onChange(newValue || undefined);
									}
								}}
								{...(options?.getOptionLabel && { getOptionLabel: options.getOptionLabel })}
								renderInput={(params) => <TextField {...params} variant="outlined" error={!!error} />}
							/>
						}
					/>
				)}
			/>
		);
	}

	return (
		<div className="manual-entry-form">
			{vm.loading && (
				<Box className="loading-overlay">
					<LoadingComponent />
					<Typography variant="h5">Submitting Job...</Typography>
				</Box>
			)}
			<Grid
				alignItems="stretch"
				alignContent="stretch"
				container
				maxWidth="md"
				margin="auto"
				component="form"
				spacing={1}
				onSubmit={vm.handleSubmit(vm.onSubmit, vm.onError)}
			>
				<Grid size={12} className="single-upload-group">
					<Accordion defaultExpanded={false}>
						{/*
						// ==========================================================================================
						// IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
						// Maintain the same order in all places for consistency.
						// ==========================================================================================
						 */}
						<AccordianSummary expandIcon={<ExpandMoreIcon />}>
							<Tooltip title="Change your Sampling configuration here" arrow>
								<Typography variant="h6">Sampling Configuration Edits</Typography>
							</Tooltip>
						</AccordianSummary>
						<AccordionDetails>
							<Grid
								container
								spacing={1}
								sx={{ alignItems: "stretch", alignContent: "stretch", maxWidth: "md", margin: "auto", width: "100%" }}
							>
								<FormField
									title="Update Deal:"
									helpText="Select a deal"
									required={isFieldRequired("deal", vm.validationContext)}
									hidden={!isFieldVisible("deal", vm.validationContext)}
									error={vm.errors.dealIds?.message}
									input={
										<Autocomplete
											fullWidth={true}
											size="small"
											options={[...props.allDeals, { id: "custom", label: "Create New Configuration" }]}
											isOptionEqualToValue={(option, value) => option.id === value.id}
											getOptionKey={(option) => option.id}
											value={
												props.allDeals.find((deal) => deal.id === vm.watch("dealIds")?.at(0)) || {
													id: "custom",
													label: "Create New Configuration",
												}
											}
											renderInput={(params) => <TextField {...params} />}
											// onBlur allows searching for the deal by name
											onBlur={(event) => {
												const inputValue = (event.target as HTMLInputElement).value;
												const selectedDeal = (props.allDeals.find(
													(deal) => vm.getDealLabelForDropdown(deal) === inputValue,
												) || {
													id: "custom",
													label: "Create New Configuration",
												}) as any;

												if (selectedDeal.id === "custom") {
													vm.setValue("dealIds", ["custom"]);
												} else {
													vm.setValue("dealIds", [selectedDeal.id]);
													vm.setValue("frequencyOfMicros", props.selectedDeal.frequencyOfMicros);
													vm.setValue("density", props.selectedDeal.density);
													vm.setValue("depth", props.selectedDeal.depth);
													const labPackage = props.selectedDeal.testPackage;
													vm.setValue(
														"testPackage",
														Array.isArray(labPackage) ? labPackage[0] : labPackage || "No test package specified",
													);
												}
											}}
											getOptionLabel={vm.getDealLabelForDropdown}
										/>
									}
								/>
								{textFormField(
									columnDisplayNames.frequencyOfMicros,
									"Frequency of Micros in the configuration",
									"frequencyOfMicros",
									{
										size: "medium",
										value: vm.watch("frequencyOfMicros"),
										onChangeCallback: vm.updateDeal,
									},
								)}
								{textFormField(columnDisplayNames.depth, "Enter the depth in inches", "depth", {
									onChangeCallback: vm.updateDeal,
								})}
								{textFormField(columnDisplayNames.density, "Enter the density of the configuration", "density", {
									onChangeCallback: vm.updateDeal,
								})}
								{textFormField(
									columnDisplayNames.testPackage,
									"Enter the lab package for the configuration",
									"testPackage",
									{
										onChangeCallback: vm.updateDeal,
									},
								)}
							</Grid>
						</AccordionDetails>
					</Accordion>
				</Grid>

				<Grid size={12} className="single-upload-group">
					<Accordion defaultExpanded={true}>
						<AccordianSummary expandIcon={<ExpandMoreIcon />}>
							<PortalTooltip title="Grower Details">
								<Typography variant="h6">Grower Details</Typography>
							</PortalTooltip>
						</AccordianSummary>
						<AccordionDetails>
							<Grid
								container
								spacing={1}
								sx={{ alignItems: "stretch", alignContent: "stretch", maxWidth: "md", margin: "auto", width: "100%" }}
							>
								<FormField
									title={"Grower Name"}
									required={isFieldRequired("grower", vm.validationContext)}
									hidden={!isFieldVisible("grower", vm.validationContext)}
									error={vm.errors.growerName?.message}
									input={
										<Controller
											name="importGrowerName"
											control={vm.control}
											rules={{
												required: isFieldRequired("grower", vm.validationContext) ? "Grower Name is Required" : false,
											}}
											render={() => (
												<Autocomplete
													fullWidth={true}
													value={vm.growerSelected}
													options={[...props.growers]}
													freeSolo
													renderInput={(params) => <TextField {...params} label="Grower Name" variant="outlined" />}
													getOptionKey={(option) => {
														if (typeof option === "string") {
															return option;
														}
														return (option as Grower).id || (option as Grower).name;
													}}
													filterOptions={(options, params) => {
														const filtered = vm.filter(options, params);

														if (
															params.inputValue !== "" &&
															!props.growers.some(
																(grower) => grower.name.toLowerCase() === params.inputValue.toLowerCase(),
															)
														) {
															filtered.push({
																id: CREATE_NEW_GROWER,
																name: `New Grower: ${params.inputValue}`,
															});
														}

														return filtered;
													}}
													getOptionLabel={(grower) => {
														if (
															vm.watch("growerIds")?.at(0) !== CREATE_NEW_GROWER &&
															vm.watch("growerIds")?.at(0) !== undefined &&
															vm.watch("growerIds")?.at(0) !== ""
														) {
															return (grower as Grower)?.name || (grower as string);
														}
														if (typeof grower === "string") {
															return grower.startsWith("New Grower") ? grower : `New Grower: ${grower}`;
														}

														return (grower as Grower)?.name ?? "";
													}}
													onChange={(event, value) => {
														if (event.type == "blur" || event.type == "keydown") {
															if (typeof value === "string") {
																const newValue = (value as string).startsWith("New Grower:")
																	? value.replace("New Grower: ", "")
																	: value;
																if (
																	newValue !== "" &&
																	!props.growers.some((grower) => grower.name.toLowerCase() === newValue.toLowerCase())
																) {
																	vm.setValue("importGrowerName", newValue);
																	vm.setValue("growerIds", [CREATE_NEW_GROWER]);
																	vm.setValue("growerPhoneNumber", "");
																	vm.setGrowerSelected({ id: CREATE_NEW_GROWER, name: value });
																	vm.trigger("importGrowerName");
																	return;
																}
																const grower = props.growers.find(
																	(grower) => grower.name.toLowerCase() === newValue.toLowerCase(),
																);
																if (grower) {
																	vm.setValue("importGrowerName", grower.name as string);
																	vm.setValue("growerIds", [grower?.id]);
																	vm.setValue("growerPhoneNumber", grower.phoneNumber || "");
																	vm.setGrowerSelected(grower as Grower);
																}
															} else if (value && value.id === CREATE_NEW_GROWER) {
																vm.setValue("importGrowerName", (value as Grower)?.name ?? "");
																vm.setValue("growerIds", [CREATE_NEW_GROWER]);
																vm.setValue("growerPhoneNumber", "");
																vm.setGrowerSelected(value);
																vm.trigger("importGrowerName");
															} else if (typeof value === "object") {
																vm.setValue("importGrowerName", (value as Grower)?.name ?? "");
																vm.setValue("growerIds", value?.id ? [value?.id] : []);
																vm.setValue("growerPhoneNumber", (value as Grower)?.phoneNumber || "");
																vm.setGrowerSelected(value as Grower);
															}
															return;
														}
														if (typeof value === "string") {
															vm.setValue("importGrowerName", value);
															vm.setValue("growerIds", [CREATE_NEW_GROWER]);
															vm.setValue("growerPhoneNumber", "");
															vm.setGrowerSelected({ id: CREATE_NEW_GROWER, name: value });
														} else if (value && value.id === CREATE_NEW_GROWER) {
															vm.setValue("importGrowerName", (value as Grower)?.name ?? "");
															vm.setValue("growerIds", [CREATE_NEW_GROWER]);
															vm.setValue("growerPhoneNumber", "");
															vm.setGrowerSelected(value);
														} else {
															vm.setValue("importGrowerName", (value as Grower)?.name ?? "");
															vm.setValue("growerIds", value?.id ? [value?.id] : []);
															vm.setValue("growerPhoneNumber", (value as Grower)?.phoneNumber || "");
															vm.setGrowerSelected(value as Grower);
														}
														vm.trigger("importGrowerName");
													}}
													autoSelect={true}
													selectOnFocus
													clearOnBlur
													handleHomeEndKeys
												/>
											)}
										/>
									}
								/>

								{textFormField("Grower Cell Number", "Enter grower's cell phone number", "growerPhoneNumber")}
							</Grid>
						</AccordionDetails>
					</Accordion>
				</Grid>

				<Grid size={12} className="single-upload-group">
					<Accordion defaultExpanded={true}>
						<AccordianSummary expandIcon={<ExpandMoreIcon />}>
							<PortalTooltip title="Job Data">
								<Typography variant="h6">Job Data</Typography>
							</PortalTooltip>
						</AccordianSummary>
						<AccordionDetails>
							<Grid
								container
								spacing={1}
								sx={{ alignItems: "stretch", alignContent: "stretch", maxWidth: "md", margin: "auto", width: "100%" }}
							>
								{/* Basic Information */}
								{textFormField(columnDisplayNames.farm, "Only required if field name is not unique", "farm", {
									registerName: "farmName",
								})}
								{textFormField(columnDisplayNames.field, "Enter the name of the field", "field", {
									registerName: "fieldName",
								})}

								{/* Attachments */}
								{fileFormField(
									columnDisplayNames.boundaryGeoJSON,
									"This is for your field boundary shp file",
									"boundarySHP",
									"boundaryGeoJSON",
									vm.watch(),
								)}
								{fileFormField(
									columnDisplayNames.zonesGeoJSON,
									"This is for your Zone shp file",
									"zonesSHP",
									"zonesGeoJSON",
									vm.watch(),
								)}
								<Controller
									name="isPointsAttached"
									control={vm.control}
									render={({ field }) => (
										<FormField
											title="Attach Points?"
											helpText="Select if you want to attach points (points should be attached for Zones and ModGrid), then upload points file"
											input={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
										/>
									)}
								/>
								{fileFormField(
									columnDisplayNames.pointsGeoJSON,
									"This is for your point shp file",
									"pointsSHP",
									"pointsGeoJSON",
									vm.watch(),
								)}
								{fileFormField(
									columnDisplayNames.fieldNotes,
									"This is for any additional field files, like pullin instructions",
									"fieldNotes",
									"fieldNotes",
									vm.watch(),
								)}

								{/* Order */}
								{checkboxFormField(
									columnDisplayNames.isReadyToSample,
									"Select if field is ready to sample now",
									"isReadyToSample",
								)}
								{dateFormField(
									columnDisplayNames.dateFieldReadyEstimated,
									"When do you estimate this field will be ready for sampling?",
									"dateFieldReadyEstimated",
								)}
								{isFieldVisible("fieldPriority", vm.validationContext) && (
									<FormField
										title={columnDisplayNames.fieldPriority}
										helpText="Select the priority of the field"
										required={isFieldRequired("fieldPriority", vm.validationContext)}
										input={
											<Select
												fullWidth
												value={vm.watch("fieldPriority")}
												{...vm.register("fieldPriority", getFormValidationRules("fieldPriority", vm.validationContext))}
											>
												{getJobPriorities().map((priority, index) => {
													return (
														<MenuItem key={index} value={priority}>
															{priority}
														</MenuItem>
													);
												})}
											</Select>
										}
									/>
								)}
								{checkboxFormField(
									columnDisplayNames.collectBoundary,
									"Select this is you want us to collect a boundary on your field",
									"collectBoundary",
								)}
								{textFormField(
									columnDisplayNames.submissionNotes,
									"Enter any notes for the submission(restrictions, special notes)",
									"submissionNotes",
									{ multiline: true, rows: 3 },
								)}

								{/* Deal */}
								{textFormField(columnDisplayNames.labName, "Enter the name of the lab", "labName")}

								{/* Lab Info */}
								{textFormField(
									columnDisplayNames.labInstructions,
									"Enter any special notes for lab",
									"labInstructions",
									{
										multiline: true,
										rows: 3,
									},
								)}
								<Controller
									name="microsList"
									control={vm.control}
									defaultValue={""}
									rules={getFormValidationRules("microsList", vm.validationContext)}
									render={({ field }) => (
										<FormField
											title={columnDisplayNames.microsList}
											helpText="Enter comma separated list of sample IDs that you want add on tests to apply to"
											required={isFieldRequired("microsList", vm.validationContext)}
											input={
												<TextField
													variant="outlined"
													fullWidth
													color={vm.errors.microsList ? "error" : "primary"}
													value={vm.microsRaw}
													onChange={(e) => vm.setMicrosRaw(e.target.value)}
													onBlur={() => {
														const parsed = vm.parseMicrosList(vm.microsRaw);
														field.onChange(parsed);
														vm.setMicrosRaw(parsed);
													}}
												/>
											}
											error={vm.errors.microsList?.message}
											hidden={!isFieldVisible("microsList", vm.validationContext)}
										/>
									)}
								/>
								{autocompleteFormField(
									columnDisplayNames.lastCrop,
									"Previous crop grown in this field",
									"lastCrop",
									props.lastCropOptions ?? [],
								)}
								{autocompleteFormField(
									columnDisplayNames.nextCrop,
									"Next crop to be planted in this field",
									"nextCrop",
									props.nextCropOptions ?? [],
								)}
								{textFormField(
									columnDisplayNames.yieldGoal,
									"Target yield for this field (e.g., '200 bu/ac')",
									"yieldGoal",
								)}

								{/* Contacts */}
								{props.branches &&
									props.branches.length > 0 &&
									autocompleteFormField<typeof vm.selectedBranch>(
										columnDisplayNames.branch,
										"Select the branch for this job",
										"branch",
										props.branches || [],
										{
											registerName: "branchIds",
											getOptionLabel: (branch) => branch?.name ?? "",
											customValue: vm.selectedBranch,
											customOnChange: (newValue, field) => {
												vm.setSelectedBranch(newValue);
												field.onChange(newValue ? [newValue.id] : []);
											},
										},
									)}
								<FormField
									title={columnDisplayNames.salesperson}
									helpText="Select or add a salesperson"
									required={isFieldRequired("salesperson", vm.validationContext)}
									hidden={!isFieldVisible("salesperson", vm.validationContext)}
									input={
										<Controller
											name="salespersonIds"
											control={vm.control}
											rules={{
												required: isFieldRequired("salesperson", vm.validationContext)
													? "Salesperson is required"
													: false,
											}}
											render={() => (
												<Autocomplete
													fullWidth
													value={vm.salespersonSelected}
													options={props.salespersons ?? []}
													freeSolo
													renderInput={(params) => <TextField {...params} label="Salesperson" variant="outlined" />}
													getOptionKey={(option) => {
														if (typeof option === "string") return option;
														return option.id || option.name;
													}}
													filterOptions={(options, params) => {
														const filtered = vm.filter(options, params);

														if (
															params.inputValue !== "" &&
															!(props.salespersons ?? []).some(
																(sp) => sp.name.toLowerCase() === params.inputValue.toLowerCase(),
															)
														) {
															filtered.push({
																id: CREATE_NEW_SALESPERSON,
																name: `New Salesperson: ${params.inputValue}`,
															});
														}

														return filtered;
													}}
													getOptionLabel={(salesperson) => {
														if (
															vm.watch("salespersonIds")?.at(0) !== CREATE_NEW_SALESPERSON &&
															vm.watch("salespersonIds")?.at(0) !== undefined &&
															vm.watch("salespersonIds")?.at(0) !== ""
														) {
															return (salesperson as any)?.name || (salesperson as string);
														}
														if (typeof salesperson === "string") {
															return salesperson.startsWith("New Salesperson")
																? salesperson
																: `New Salesperson: ${salesperson}`;
														}

														return (salesperson as any)?.name ?? "";
													}}
													onChange={(event, value) => {
														if (typeof value === "string") {
															const newValue = value.startsWith("New Salesperson:")
																? value.replace("New Salesperson: ", "")
																: value;

															if (
																newValue !== "" &&
																!(props.salespersons ?? []).some(
																	(sp) => sp.name.toLowerCase() === newValue.toLowerCase(),
																)
															) {
																vm.setValue("salespersonIds", [CREATE_NEW_SALESPERSON]);
																vm.setValue("newSalesperson", { name: newValue, phone: "" });
																vm.setSalespersonSelected({ id: CREATE_NEW_SALESPERSON, name: value });
																vm.trigger("salespersonIds");
																vm.trigger("newSalesperson.phone");
																return;
															}

															const salesperson = (props.salespersons ?? []).find(
																(sp) => sp.name.toLowerCase() === newValue.toLowerCase(),
															);
															if (salesperson) {
																vm.setValue("salespersonIds", [salesperson.id]);
																vm.setValue("newSalesperson", undefined);
																vm.setSalespersonSelected(salesperson);
																vm.trigger("newSalesperson.phone");
															}
														} else if (value && value.id === CREATE_NEW_SALESPERSON) {
															const name = (value as any).name.replace("New Salesperson: ", "");
															vm.setValue("salespersonIds", [CREATE_NEW_SALESPERSON]);
															vm.setValue("newSalesperson", { name, phone: "" });
															vm.setSalespersonSelected(value);
															vm.trigger("salespersonIds");
															vm.trigger("newSalesperson.phone");
														} else if (value) {
															vm.setValue("salespersonIds", [value.id]);
															vm.setValue("newSalesperson", undefined);
															vm.setSalespersonSelected(value as any);
															vm.trigger("newSalesperson.phone");
														} else {
															vm.setValue("salespersonIds", []);
															vm.setValue("newSalesperson", undefined);
															vm.setSalespersonSelected(null);
															vm.trigger("newSalesperson.phone");
														}
													}}
													autoSelect
													selectOnFocus
													clearOnBlur
													handleHomeEndKeys
												/>
											)}
										/>
									}
								/>
								<FormField
									title={columnDisplayNames.salespersonMobilePhone}
									helpText="Salesperson mobile phone number"
									required={isFieldRequired("salespersonMobilePhone", vm.validationContext)}
									hidden={!isFieldVisible("salespersonMobilePhone", vm.validationContext)}
									error={
										vm.watch("salespersonIds")?.at(0) === CREATE_NEW_SALESPERSON
											? vm.errors.newSalesperson?.phone?.message
											: undefined
									}
									input={
										<Controller
											name="newSalesperson.phone"
											control={vm.control}
											rules={
												vm.watch("salespersonIds")?.at(0) === CREATE_NEW_SALESPERSON
													? getFormValidationRules("salespersonMobilePhone", vm.validationContext)
													: {}
											}
											render={({ field }) => (
												<TextField
													{...field}
													variant="outlined"
													fullWidth
													disabled={vm.watch("salespersonIds")?.at(0) !== CREATE_NEW_SALESPERSON}
													value={
														vm.watch("salespersonIds")?.at(0) === CREATE_NEW_SALESPERSON
															? field.value || ""
															: (props.salespersons ?? []).find((sp) => sp.id === vm.watch("salespersonIds")?.at(0))
																	?.mobilePhone || ""
													}
													onChange={(e) => {
														if (vm.watch("salespersonIds")?.at(0) === CREATE_NEW_SALESPERSON) {
															field.onChange(e.target.value);
														}
													}}
													placeholder={
														vm.watch("salespersonIds")?.at(0) === CREATE_NEW_SALESPERSON ? "Enter phone number" : ""
													}
													error={
														vm.watch("salespersonIds")?.at(0) === CREATE_NEW_SALESPERSON &&
														!!vm.errors.newSalesperson?.phone
													}
												/>
											)}
										/>
									}
								/>
								{textFormField(
									columnDisplayNames.submitterName,
									"Name of the person submitting this job",
									"submitterName",
								)}
								{textFormField(
									columnDisplayNames.submitterEmail,
									"Email address of the person submitting this job",
									"submitterEmail",
									{
										type: "email",
									},
								)}

								{/* Sync Codes */}
								{textFormField(
									columnDisplayNames.eventId,
									"This is used to sync your data with the appropriate software",
									"eventId",
								)}
								{textFormField(
									columnDisplayNames.labSubmissionCode,
									"This is the code for the lab submission",
									"labSubmissionCode",
								)}
								{textFormField(columnDisplayNames.fieldId, "Unique identifier for this field", "fieldId")}
								{textFormField(columnDisplayNames.externalId, "Enter the external ID", "externalId")}
								{textFormField(columnDisplayNames.externalFarmId, "Enter the external farm ID", "externalFarmId")}
								{textFormField(columnDisplayNames.externalLabId, "Enter the external lab ID", "externalLabId")}

								{/* Misc */}
								{checkboxFormField(columnDisplayNames.isTestJob, "Enable if this is a testing job", "isTestJob")}
							</Grid>
						</AccordionDetails>
					</Accordion>
				</Grid>
				<div className="required-fields-note">
					<Typography>
						{" "}
						Fields marked with <span className="required-asterisk">*</span> are required
					</Typography>
				</div>

				<Grid size={12} className="single-upload-group">
					<Button fullWidth type="submit" variant="contained" color="primary" disabled={!vm.isValid}>
						<Publish className="icon-in-button" />
						Create Job
					</Button>
				</Grid>
			</Grid>
		</div>
	);
}
